import React from 'react';
import classnames from 'classnames';
import deepmerge from 'deepmerge';

/* CURRENT COMPONENT FILES */
import styles from './index.module.less';
import modifiersToClassNames from '../../../core/modifiersToClassNames';
import { VAlign, Color, Align } from '../../../core/enums';
import type { Theme } from '../../../core/types';
import { Icon } from '../../atoms/icon';
import Element from '../../../core/Element';

export interface BulletPoint {
  icon?: {
    directory: string;
    imageName: string;
  };
  text: string;
  title?: string;
}

export interface Customization {
  icon?: Theme;
  listContainer?: Theme;
  listItem?: Theme;
  listTitle?: Theme;
}
interface Props {
  data: {
    bulletPoints: BulletPoint[];
  };
  $iconAlign?: VAlign;
  customization?: Customization;
  qeId?: string;
}

const defaultCustomization: Customization = {
  listContainer: {},
  icon: {
    color: Color.DeepBlue,
  },
  listItem: {
    color: Color.Gray06,
    textBody: 1,
  },
};

export const IconBulletedList = ({
  data: { bulletPoints },
  $iconAlign = VAlign.Center,
  customization = {},
  qeId = 'IconBulletedList',
}: Props): React.ReactElement => {
  const customizationObj = deepmerge(defaultCustomization, customization);
  return (
    <Element
      Component="ul"
      data-qe-id={qeId}
      className={styles.root}
      theme={customizationObj.listContainer}
    >
      {bulletPoints.map(({ icon, text, title }, i) => (
        <Element
          key={`${i}_${text}`}
          Component="li"
          className={styles.item}
          theme={{ paddingTop: 10, paddingBottom: 10 }}
        >
          {title && (
            <Element
              Component="h2"
              theme={{
                textBody: 2,
                paddingBottom: 10,
                margin: 0,
                color: Color.DeepBlue,
                ...customizationObj.listTitle,
              }}
            >
              {title}
            </Element>
          )}
          <Element
            className={classnames(
              styles.textRow,
              modifiersToClassNames(styles, {
                $iconAlign,
              })
            )}
          >
            {icon && <Icon {...icon} theme={customizationObj.icon} />}
            <Element
              Component="span"
              theme={{
                paddingLeft: icon ? 15 : 0,
                textAlign: Align.Left,
                horizontallyFluid: true,
                ...customizationObj.listItem,
              }}
            >
              {text}
            </Element>
          </Element>
        </Element>
      ))}
    </Element>
  );
};
